import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface BasesModalProps {
  open: boolean;
  onClose: () => void;
}

const BasesModal: React.FC<BasesModalProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Bloquear scroll del body mientras el modal está abierto
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="bases-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bases-modal-title"
    >
      <div
        className="bases-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="bases-modal-close"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <X size={20} />
        </button>

        <div className="bases-modal-content">
          <header className="bases-modal-header">
            <p className="bases-modal-kicker">Bases Reguladoras</p>
            <h2 id="bases-modal-title" className="bases-modal-title text-gold-shine">
              Premios NovaGob Excelencia 2026
            </h2>
          </header>

          <section className="bases-modal-section">
            <h3>Presentación. ¿Qué son los Premios NovaGob?</h3>
            <p>
              Desde su primera edición en 2015, los Premios NovaGob Excelencia
              contribuyen a visibilizar proyectos e iniciativas innovadoras
              desarrolladas en las Administraciones Públicas, consolidándose
              como un espacio de referencia para reconocer experiencias pioneras
              de transformación del sector público y a las personas que
              impulsan la innovación pública.
            </p>
            <p>
              Los Premios NovaGob Excelencia 2026 están organizados por la{' '}
              <strong>Fundación NovaGob</strong> y se conceden mediante una
              convocatoria abierta, seguida de un proceso de validación de
              candidaturas, evaluación por un jurado independiente y votación
              de la comunidad NovaGob entre las candidaturas finalistas.
            </p>
            <p>
              Las candidaturas finalmente galardonadas recogerán el premio en
              la Gala de los Premios NovaGob que se celebrará en Madrid el
              próximo <strong>10 de diciembre de 2026</strong>.
            </p>
          </section>

          <section className="bases-modal-section">
            <h3>Estructura territorial de los premios</h3>
            <p>
              Con el objetivo de reforzar la visibilidad de la innovación
              pública en distintos contextos administrativos, los Premios
              NovaGob Excelencia 2026 se estructuran en dos ámbitos geográficos
              de participación:
            </p>
            <h4>Premios NovaGob España</h4>
            <p>
              Dirigidos a reconocer proyectos e iniciativas desarrolladas por
              Administraciones Públicas españolas.
            </p>
            <h4>Premios NovaGob LATAM</h4>
            <p>
              Dirigidos a reconocer iniciativas y profesionales de la
              innovación pública en América Latina.
            </p>
            <p>
              Ambos ámbitos comparten el mismo proceso de presentación,
              evaluación y votación.
            </p>
          </section>

          <section className="bases-modal-section">
            <h3>Comité Organizador</h3>
            <p>
              Todo el proceso que implica la celebración de los Premios NovaGob
              Excelencia será coordinado por el Comité Organizador. Nombrado
              por la Fundación NovaGob, es un órgano técnico encargado de:
            </p>
            <ul>
              <li>Interpretar las bases reguladoras.</li>
              <li>Garantizar el correcto desarrollo del proceso.</li>
              <li>Validar que las candidaturas cumplen los requisitos establecidos.</li>
            </ul>
            <p>
              El Comité Organizador estará compuesto por entre tres y cinco
              personas y su composición será pública. No puede participar de
              las candidaturas o intervenir en la decisión del Jurado.
            </p>
          </section>

          <section className="bases-modal-section">
            <h3>Estructura de los premios</h3>
            <p>Los Premios NovaGob Excelencia constan de cuatro fases:</p>
            <ol>
              <li>Presentación y validación de candidaturas.</li>
              <li>Evaluación del jurado.</li>
              <li>Votación de la comunidad.</li>
              <li>Confirmación de ganadores y entrega de reconocimientos en la Gala.</li>
            </ol>
          </section>

          <section className="bases-modal-section">
            <h3>Fase 1 — Presentación y validación de candidaturas</h3>
            <p>
              La presentación de candidaturas podrá realizarse por cualquier
              persona física que cumpla con los siguientes requisitos:
            </p>
            <ul>
              <li>
                Tener una vinculación profesional con la candidatura propuesta
                (este aspecto no se aplica para las categorías "Persona
                Innovadora del Año", "Mujer Destacada del Sector Público" o
                "Nuevo Talento Público").
              </li>
              <li>
                Se podrán presentar tantas y diferentes candidaturas como se
                desee en las distintas categorías. Pero una misma candidatura
                no se podrá presentar a más de una categoría.
              </li>
              <li>
                Para considerarse válida, la candidatura debe rellenar todos
                los campos requeridos del formulario, con la información en
                el formato y extensión establecidos.
              </li>
              <li>
                A todas las candidaturas aceptadas se les dará la
                correspondiente difusión, de forma que sean públicas y
                conocidas.
              </li>
              <li>
                El Comité Organizador se reserva el derecho de retirar
                cualquier candidatura por incumplimiento de estas bases o por
                ser contraria al Decálogo Ético.
              </li>
              <li>
                La presentación de candidaturas implica la aceptación completa
                de las bases de esta convocatoria.
              </li>
              <li>
                Las candidaturas presentadas fuera de plazo serán desestimadas.
              </li>
              <li>
                Los Premios se circunscriben al ámbito de las Administraciones
                Públicas.
              </li>
            </ul>
          </section>

          <section className="bases-modal-section">
            <h3>Categorías Premios NovaGob España</h3>

            <h4>1) Persona Innovadora del Año</h4>
            <p>
              Destaca a personas del sector público que hayan demostrado un
              compromiso excepcional con la innovación pública durante el
              último año. Se valorará su capacidad para impulsar iniciativas
              innovadoras, promover nuevas formas de gestión pública, difundir
              el conocimiento y generar impacto positivo. Se podrá presentar
              por autopostulación o ser propuesta por terceros.
            </p>

            <h4>2) Mujer Destacada del Sector Público</h4>
            <p>
              Reconoce y visibiliza el papel de las mujeres en la
              transformación y modernización de las administraciones públicas.
              Distingue a mujeres que hayan destacado por su liderazgo,
              trayectoria o contribución a la innovación pública.
            </p>

            <h4>3) Nuevo Talento Público</h4>
            <p>
              Identifica y visibiliza el talento emergente dentro del sector
              público. Dirigido a personas incorporadas a la administración
              pública en los últimos diez años que hayan demostrado liderazgo,
              iniciativa y compromiso con la innovación y mejora de los
              servicios públicos.
            </p>

            <h4>4) Creatividad en la innovación</h4>
            <p>
              Pone en valor la dimensión creativa de los procesos de innovación
              en las administraciones públicas. Reconoce iniciativas que
              destacan por la generación de nuevas ideas, enfoques,
              metodologías o herramientas innovadoras, así como por replantear
              problemas públicos desde perspectivas originales.
            </p>

            <h4>5) Proyecto más transformador</h4>
            <p>
              Premia proyectos o políticas públicas que hayan generado cambios
              significativos en la organización, en la prestación de servicios
              o en la relación con la ciudadanía. Se valorarán iniciativas de
              cualquier ámbito con impacto relevante.
            </p>

            <h4>6) Proyecto que haya generado mayor eficiencia</h4>
            <p>
              Destaca proyectos que hayan permitido mejorar la eficiencia de
              las administraciones públicas mediante optimización de procesos,
              simplificación administrativa o uso innovador de recursos:
              hacer más con los mismos o menos recursos.
            </p>

            <h4>7) Transformación digital</h4>
            <p>
              Reconoce proyectos que impulsen la modernización tecnológica:
              inteligencia artificial, análisis de datos, automatización,
              soluciones en la nube u otras herramientas digitales que mejoren
              la accesibilidad, eficiencia y calidad de los servicios
              públicos, con un uso ético y responsable de la tecnología.
            </p>

            <h4>8) Gestión de personas en las administraciones públicas</h4>
            <p>
              Premia iniciativas orientadas a mejorar la gestión del talento:
              desarrollo profesional, gestión por competencias, atracción y
              retención de talento, cultura organizativa, liderazgo público,
              igualdad de oportunidades o bienestar de las personas empleadas
              públicas.
            </p>

            <h4>9) Buen gobierno</h4>
            <p>
              Reconoce iniciativas que promuevan la transparencia, la
              integridad pública, la rendición de cuentas y la participación
              ciudadana. Proyectos innovadores en gobierno abierto,
              comunicación pública, control interno o contratación pública.
            </p>

            <p>
              En cada categoría el jurado seleccionará tres candidaturas
              finalistas que pasarán a la fase de votación de la comunidad.
            </p>
          </section>

          <section className="bases-modal-section">
            <h3>Premios NovaGob LATAM</h3>
            <h4>1) Persona Innovadora LATAM</h4>
            <p>
              Distingue a profesionales del sector público en América Latina
              que hayan destacado por su compromiso con la innovación pública,
              liderazgo y capacidad para impulsar proyectos transformadores.
            </p>
            <h4>2) Proyecto Innovador LATAM</h4>
            <p>
              Reconoce proyectos de administraciones públicas latinoamericanas
              que hayan generado mejoras significativas en la gestión pública
              o en la prestación de servicios, valorando impacto, carácter
              innovador, valor público y replicabilidad.
            </p>
            <p>
              El jurado seleccionará <strong>tres</strong> candidaturas
              finalistas en cada categoría que pasarán a la votación de la
              comunidad, y se confirmarán los ganadores de cada categoría el
              10 de diciembre en la Gala de los Premios NovaGob 2026.
            </p>
          </section>

          <section className="bases-modal-section">
            <h3>Categorías especiales</h3>
            <h4>Categoría especial</h4>
            <p>
              El Comité Organizador y las entidades colaboradoras podrán
              reconocer a personas u organizaciones que destaquen por su
              trayectoria o impacto en la innovación pública.
            </p>
            <h4>Distinción especial: Impacto y Replicabilidad de la Innovación Pública</h4>
            <p>
              Reconocimiento a proyectos premiados en ediciones anteriores que
              hayan demostrado:
            </p>
            <ul>
              <li>Impacto sostenido.</li>
              <li>Replicabilidad en otras administraciones.</li>
              <li>Capacidad de inspirar mejoras en la gestión pública.</li>
            </ul>
            <p>
              Esta distinción será otorgada por decisión motivada del Comité
              Organizador. La presentación de candidaturas se realizará en el
              plazo indicado para las demás categorías de los Premios.
            </p>
            <p>
              <strong>Criterios de valoración:</strong>
            </p>
            <ul>
              <li>
                <em>Efecto multiplicador de buenas prácticas:</em> se evaluará
                cuántas organizaciones o unidades internas han adoptado la
                experiencia premiada tras realizar benchmarking.
              </li>
              <li>
                <em>Impacto positivo comprobado:</em> evidencias de mejora en
                eficiencia, calidad del servicio, satisfacción de los
                destinatarios y alcance de los servicios públicos.
              </li>
            </ul>
          </section>

          <section className="bases-modal-section">
            <h3>Fase 2 — Evaluación del jurado</h3>
            <p>
              El Jurado seleccionará las tres mejores propuestas de todas las
              candidaturas en cada categoría, que pasarán a la fase de
              votación de la comunidad.
            </p>
            <h4>Composición</h4>
            <p>
              El Jurado está compuesto por las personas premiadas en la
              edición anterior, a las que pueden unirse, si así lo decide el
              Comité Organizador, otras personas de reconocido prestigio. Su
              composición será pública en la web de los Premios.
            </p>
            <h4>Funciones, transparencia e integridad</h4>
            <ul>
              <li>Evaluar y valorar las candidaturas en cada categoría.</li>
              <li>
                El documento con la valoración incluirá las puntuaciones por
                indicador, otorgadas por cada miembro del jurado, y el
                sumatorio final que determinará las candidaturas finalistas.
              </li>
              <li>El fallo se hará público por los canales oficiales.</li>
              <li>
                Las personas integrantes del Jurado son independientes y no
                pueden formar parte del Comité Organizador.
              </li>
              <li>
                Si la institución de un miembro del jurado se presenta a los
                premios, esa persona se inhibirá de la categoría
                correspondiente.
              </li>
            </ul>
            <h4>Criterios</h4>
            <ul>
              <li>
                <strong>Categorías nominales:</strong> trayectoria profesional
                y académica, actividades destacadas en el último año,
                compromiso con la difusión de la innovación pública y la
                transferencia de conocimiento.
              </li>
              <li>
                <strong>Creatividad, conocimiento e innovación:</strong>
                novedad del servicio o iniciativa, complejidad de gestión del
                conocimiento y nivel de innovación.
              </li>
              <li>
                <strong>Transferencia y difusión:</strong> capacidad de ser
                aplicada, adaptada o compartida con otras organizaciones;
                acciones de benchmarking.
              </li>
              <li>
                <strong>Impacto sobre la ciudadanía:</strong> efectos sobre
                usuarios y grupos de interés, magnitud de la población
                beneficiada, beneficios obtenidos (reducción de tiempos,
                trámites, costes…), mejora de satisfacción y facilidad de
                acceso.
              </li>
              <li>
                <strong>Sostenibilidad de la iniciativa:</strong> nivel de
                consolidación o institucionalización.
              </li>
              <li>
                <strong>Cambio en la cultura organizativa:</strong>
                implicación de las personas de la organización y colaboración
                entre profesionales.
              </li>
              <li>
                <strong>Equilibrio de eficacia y eficiencia:</strong>
                consecución de objetivos y optimización de procesos y
                recursos.
              </li>
              <li>
                <strong>Evaluación de la iniciativa:</strong> mecanismos de
                evaluación, escucha activa y medición constante de la calidad.
              </li>
            </ul>
          </section>

          <section className="bases-modal-section">
            <h3>Fase 3 — Votación de la comunidad</h3>
            <p>
              Se abrirá un sistema de votación telemática. Podrán votar
              personas empleadas públicas y especialistas en administración
              pública del ámbito académico y privado. De las tres finalistas,
              la comunidad escogerá dos por categoría.
            </p>
            <ul>
              <li>Se podrá emitir un solo voto por categoría.</li>
              <li>
                El uso de multicuentas no está permitido. El Comité Organizador
                podrá anular votos fraudulentos, spam o generados por bots, e
                invalidar candidaturas involucradas.
              </li>
              <li>
                Incidencias: <a href="mailto:info@novagob.org">info@novagob.org</a>.
              </li>
              <li>
                Tras finalizar la fase de votaciones, el sistema quedará
                cerrado.
              </li>
            </ul>
          </section>

          <section className="bases-modal-section">
            <h3>Fase 4 — Gala de los Premios NovaGob Excelencia 2026</h3>
            <p>
              La proclamación de la candidatura ganadora y el accésit de cada
              categoría se comunicarán en la Gala el 10 de diciembre de 2026
              en Madrid.
            </p>
            <ul>
              <li>
                Reconocimiento físico expedido por la Fundación NovaGob,
                entregado en la Gala.
              </li>
              <li>
                La organización no cubre los gastos de desplazamiento,
                alojamiento ni manutención asociados a la asistencia a la
                Gala.
              </li>
            </ul>
          </section>

          <section className="bases-modal-section">
            <h3>Plazos — Edición 2026</h3>
            <ul>
              <li>
                <strong>Exposición de las bases:</strong> del 27 de abril al 4
                de mayo (12:00 h, hora Madrid-España).
              </li>
              <li>
                <strong>Presentación de candidaturas:</strong> del 4 de mayo
                al 3 de julio (12:00 h).
              </li>
              <li>
                <strong>Proclamación de candidaturas y evaluación del
                jurado:</strong> del 13 de julio al 4 de septiembre.
              </li>
              <li>
                <strong>Proclamación de las 3 candidaturas mejor valoradas y
                votación de la comunidad:</strong> del 8 al 22 de septiembre
                (12:00 h).
              </li>
              <li>
                <strong>Proclamación de candidaturas finalistas:</strong> 28
                de septiembre.
              </li>
              <li>
                <strong>Gala de los Premios:</strong> 10 de diciembre de 2026
                en Madrid.
              </li>
            </ul>
            <p style={{ fontSize: 13, fontStyle: 'italic', marginTop: 12 }}>
              * El calendario podrá experimentar cambios que serán comunicados
              previamente por parte de la organización de los Premios por los
              canales pertinentes.
            </p>
          </section>

          <footer className="bases-modal-footer">
            <a
              href="/bases-premios-novagob-2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bases-modal-download"
              download
            >
              Descargar PDF oficial
            </a>
            <div className="bases-modal-footer-credits">
              Organiza: Fundación NovaGob · Anfitrión: Madrid Talento
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default BasesModal;
